<template>
  <main-template>
    <!-- Header -->
    <v-app-bar color="#1e40af" height="120" flat>
      <v-container class="text-center white--text">
        <h1 class="text-h5 font-weight-bold">📚 Calculadora FUNDEB 2025</h1>
        <p class="text-subtitle-2 mt-1" style="color: #bfdbfe">
          Sistema de Cálculo de Incremento do Orçamento Educacional
        </p>
      </v-container>
    </v-app-bar>

    <v-container class="py-8" max-width="1200">
      <!-- Formulário -->
      <v-card class="pa-6 mb-8 rounded-lg shadow-sm">
        <!-- Seção 1 -->
        <v-card-title class="text-h6 font-weight-bold align-center d-flex">
          <v-avatar color="#1e40af" size="32" class="mr-3">
            <span class="white--text">1</span>
          </v-avatar>
          Dados do Município
        </v-card-title>

        <v-divider class="mb-4"></v-divider>

        <v-card-text>
          <ValidationObserver v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(calcularOrcamento)" :disabled="submittingForm">
              <v-row dense>
                <v-col class="pt-0 pb-0" cols="6" md="6">
                  <ValidationProvider name="Nome do Município" rules="required" v-slot="{ errors }">
                    <e-label>Nome do Município *</e-label>
                    <v-text-field
                      v-model="municipio.nome"
                      variant="outlined"
                      density="comfortable"
                      required
                      dense
                      solo
                      :error-messages="errors"
                      @input="municipio.nome = municipio.nome.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col class="pt-0 pb-0" cols="6" md="6">
                  <e-label>UF *</e-label>
                  <ValidationProvider name="UF" rules="required" v-slot="{ errors }">
                    <e-autocomplete
                      v-model="municipio.uf"
                      :items="ufs"
                      variant="outlined"
                      density="comfortable"
                      required
                      dense
                      solo
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col class="pt-0 pb-0" cols="12" md="6">
                  <ValidationProvider
                    name="Receitas da Educação (R$)"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <e-label>Receitas da Educação (R$) *</e-label>
                    <v-text-field
                      v-model.number="municipio.receitas_educacao"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                      required
                      dense
                      solo
                      :error-messages="errors"
                      min="0"
                      @input="
                        municipio.receitas_educacao = Math.max(0, municipio.receitas_educacao || 0)
                      "
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <!-- Matrículas -->
              <h3
                class="d-flex align-center text-subtitle-1 font-weight-medium text-grey-darken-3 mb-4 mt-6"
              >
                <v-avatar color="green" size="28" class="mr-2">
                  <span class="text-body-2">📊</span>
                </v-avatar>
                Matrículas por Segmento de Ensino
              </h3>

              <v-row dense>
                <v-col
                  v-for="(segmento, key) in fatoresPonderacao"
                  :key="key"
                  cols="3"
                  md="4"
                  class="pt-0 pb-0"
                >
                  <e-label>{{ segmento.nome }} </e-label> <br />
                  <e-label class="text-caption">
                    VAAF: {{ segmento.fp_vaaf }} | VAAT: {{ segmento.fp_vaat }}
                  </e-label>
                  <v-text-field
                    v-model.number="municipio.matriculas_por_segmento[key]"
                    persistent-hint
                    type="number"
                    min="0"
                    variant="outlined"
                    density="comfortable"
                    dense
                    solo
                  />
                </v-col>
              </v-row>

              <!-- Condicionalidades -->
              <h3
                class="d-flex align-center text-subtitle-1 font-weight-medium text-grey-darken-3 mb-4 mt-6"
              >
                <v-avatar color="yellow darken-4" size="28" class="mr-2 white--text">
                  <span class="text-body-2">✓</span>
                </v-avatar>
                Condicionalidades VAAR
              </h3>

              <v-checkbox
                v-for="(cond, index) in condicionalidades"
                :key="index"
                v-model="municipio.condicionalidades[index]"
                hide-details
                density="comfortable"
                color="#1e40af"
              >
                <template #label>
                  <span v-html="cond"></span>
                </template>
              </v-checkbox>

              <div class="text-center mt-6">
                <v-btn
                  class="white--text px-8"
                  color="#1e40af"
                  :loading="submittingForm"
                  :disabled="submittingForm"
                  type="submit"
                  size="large"
                >
                  🧮 Calcular Orçamento FUNDEB
                </v-btn>
              </div>
            </form>
          </ValidationObserver>
        </v-card-text>
      </v-card>

      <!-- Resultados -->
      <v-slide-y-transition>
        <div v-if="resultado">
          <!-- Resumo Executivo -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold align-center d-flex mb-4">
              <v-avatar color="green" size="32" class="mr-3">
                <span class="white--text">2</span>
              </v-avatar>
              Resumo Executivo
            </v-card-title>

            <v-row dense>
              <v-col cols="12" md="3" v-for="item in resumo" :key="item.titulo">
                <v-sheet
                  class="pa-4 rounded-lg text-left"
                  elevation="1"
                  :color="item.color || 'blue-lighten-5'"
                  style="border-left: 4px solid"
                  :style="`border-left-color: ${item.borderColor || '#2196F3'}`"
                >
                  <div class="text-caption text-grey-darken-1 mb-1">
                    {{ item.titulo }}
                  </div>
                  <div
                    class="text-h6 font-weight-bold"
                    :style="`color: ${item.valueColor || '#000'}`"
                  >
                    {{ item.valor }}
                  </div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card>

          <!-- Detalhamento dos Complementos -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold mb-4">
              💰 Detalhamento dos Complementos
            </v-card-title>

            <v-card-text class="pa-0">
              <div v-for="(comp, key) in resultado.complementos" :key="key" class="mb-3">
                <v-sheet
                  class="pa-4 rounded-lg d-flex justify-space-between align-center"
                  elevation="1"
                  :color="comp.recebe ? 'light-green lighten-5' : 'grey-lighten-4'"
                >
                  <div>
                    <div class="font-weight-bold text-body-1">
                      {{ comp.label }}
                    </div>
                    <div class="text-caption text-grey-darken-1">
                      {{ comp.mensagem }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-h6 font-weight-bold">
                      {{ formatarMoeda(comp.valor) }}
                    </div>
                    <v-chip :color="comp.recebe ? 'green' : 'grey'" size="small" variant="flat">
                      {{ comp.recebe ? "Elegível" : "Não Elegível" }}
                    </v-chip>
                  </div>
                </v-sheet>
              </div>
            </v-card-text>
          </v-card>

          <!-- Estratégias para Ampliar Orçamento -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold mb-4">
              🎯 Estratégias para Ampliar Orçamento
            </v-card-title>

            <v-card-text class="pa-0">
              <!-- Maximizar Fatores de Ponderação -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="light-blue lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <span class="font-weight-bold text-body-1 text-blue"
                    >🎯 Maximizar Fatores de Ponderação</span
                  >
                </div>
                <v-list density="compact" class="bg-transparent" color="light-blue lighten-5">
                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Priorizar creche integral pública (fator 1,9 VAAT)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Expandir pré-escola integral pública (fator 1,88 VAAT)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Implementar ensino fundamental integral (fator 1,5)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Desenvolver ensino médio integral (fator 1,52)</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-sheet>

              <!-- Modalidades Especiais -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="light-green lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="green" class="mr-2">mdi-school</v-icon>
                  <span class="font-weight-bold text-body-1"
                    >Modalidades Especiais (Fatores Elevados)</span
                  >
                </div>
                <v-list density="compact" class="bg-transparent" color="light-green lighten-5">
                  <v-list-item
                    v-for="modalidade in modalidadesEspeciais"
                    :key="modalidade"
                    class="px-0"
                  >
                    <template v-slot:prepend>
                      <v-icon size="small" color="green">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ modalidade }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-sheet>

              <!-- Status das Condicionalidades VAAR -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="yellow lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <span class="font-weight-bold text-body-1"
                    >✅ Status das Condicionalidades VAAR</span
                  >
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[0] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[0] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Gestão Escolar</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[1] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[1] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Redução de Desigualdades</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[2] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[2] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Currículo BNCC</span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[3] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[3] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Participação dos Estudantes</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[4] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[4] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Regime de Colaboração</span>
                    </div>
                  </v-col>
                </v-row>
              </v-sheet>

              <!-- Oportunidades de Complementos -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="indigo lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="purple" class="mr-2">mdi-star</v-icon>
                  <span class="font-weight-bold text-body-1">Oportunidades de Complementos</span>
                </div>
                <div v-for="oportunidade in oportunidades" :key="oportunidade.tipo" class="mb-2">
                  <v-sheet
                    class="pa-3 rounded d-flex justify-space-between align-center"
                    :color="
                      oportunidade.tipo.includes('❌ Não elegível para VAAR')
                        ? 'red lighten-5'
                        : 'green lighten-5'
                    "
                    elevation="0"
                  >
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{ oportunidade.tipo }}</span>
                    </div>
                    <span class="font-weight-bold">{{ oportunidade.valor }}</span>
                  </v-sheet>
                  <div
                    v-if="!oportunidade.elegivel && oportunidade.observacao"
                    class="text-caption text-red mt-1 ml-6"
                  >
                    {{ oportunidade.observacao }}
                  </div>
                </div>
              </v-sheet>

              <!-- Análise das Matrículas Atuais -->
              <v-sheet class="pa-4 rounded-lg" color="indigo lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="blue-grey" class="mr-2">mdi-chart-bar</v-icon>
                  <span class="font-weight-bold text-indigo darken-2"
                    >Análise das Matrículas Atuais</span
                  >
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="text-body-2 font-weight-medium mb-2">Principais Segmentos</div>
                    <div
                      v-for="(quantidade, segmento) in analiseMatriculas.segmentos"
                      :key="segmento"
                      class="mb-1"
                    >
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-xs font-medium">
                          {{ fatoresPonderacao[segmento]?.nome || segmento }}
                        </span>
                        <span class="text-xs font-medium">
                          {{ quantidade }} ({{ calcularPercentual(quantidade) }}%)
                        </span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-body-2 font-weight-medium mb-2">Resumo Geral</div>
                    <div class="text-body-2 mb-1">
                      <span class="font-weight-medium">Total de Matrículas:</span>
                      <span class="float-right">{{ analiseMatriculas.total }}</span>
                    </div>
                    <div class="text-body-2 mb-1">
                      <span class="font-weight-medium">Matrículas Pond. VAAF:</span>
                      <span class="float-right">{{ analiseMatriculas.vaaf.toFixed(2) }}</span>
                    </div>
                    <div class="text-body-2">
                      <span class="font-weight-medium">Matrículas Pond. VAAT:</span>
                      <span class="float-right">{{ analiseMatriculas.vaat.toFixed(2) }}</span>
                    </div>
                  </v-col>
                </v-row>
              </v-sheet>
            </v-card-text>
          </v-card>
        </div>
      </v-slide-y-transition>
    </v-container>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      submittingForm: false,
      valid: false,
      municipio: {
        nome: "",
        uf: "",
        receitas_educacao: null,
        matriculas_por_segmento: {},
        condicionalidades: [false, false, false, false, false],
      },
      fatoresPonderacao: {
        creche_integral_publica: { nome: "Creche Integral Pública", fp_vaaf: 1.55, fp_vaat: 1.9 },
        creche_integral_conveniada: {
          nome: "Creche Integral Conveniada",
          fp_vaaf: 1.45,
          fp_vaat: 1.81,
        },
        creche_parcial_publica: { nome: "Creche Parcial Pública", fp_vaaf: 1.25, fp_vaat: 1.6 },
        creche_parcial_conveniada: {
          nome: "Creche Parcial Conveniada",
          fp_vaaf: 1.15,
          fp_vaat: 1.27,
        },
        pre_escola_integral_publica: {
          nome: "Pré-Escola Integral Pública",
          fp_vaaf: 1.5,
          fp_vaat: 1.88,
        },
        pre_escola_integral_conveniada: {
          nome: "Pré-Escola Integral Conveniada",
          fp_vaaf: 1.4,
          fp_vaat: 1.75,
        },
        pre_escola_parcial_publica: {
          nome: "Pré-Escola Parcial Pública",
          fp_vaaf: 1.15,
          fp_vaat: 1.5,
        },
        pre_escola_parcial_conveniada: {
          nome: "Pré-Escola Parcial Conveniada",
          fp_vaaf: 1.05,
          fp_vaat: 1.16,
        },
        ef_anos_iniciais_urbano: {
          nome: "Ens. Fund. Anos Iniciais Urbano",
          fp_vaaf: 1.0,
          fp_vaat: 1.0,
        },
        ef_anos_iniciais_rural: {
          nome: "Ens. Fund. Anos Iniciais Rural",
          fp_vaaf: 1.15,
          fp_vaat: 1.15,
        },
        ef_anos_finais_urbano: {
          nome: "Ens. Fund. Anos Finais Urbano",
          fp_vaaf: 1.1,
          fp_vaat: 1.1,
        },
        ef_anos_finais_rural: {
          nome: "Ens. Fund. Anos Finais Rural",
          fp_vaaf: 1.27,
          fp_vaat: 1.27,
        },
        ef_integral: {
          nome: "Ensino Fundamental Integral",
          fp_vaaf: 1.5,
          fp_vaat: 1.5,
        },
        em_urbano: {
          nome: "Ensino Médio Urbano",
          fp_vaaf: 1.25,
          fp_vaat: 1.25,
        },
        em_rural: {
          nome: "Ensino Médio Rural",
          fp_vaaf: 1.44,
          fp_vaat: 1.44,
        },
        em_integral: {
          nome: "Ensino Médio Integral",
          fp_vaaf: 1.52,
          fp_vaat: 1.52,
        },
        educacao_especial_creche: {
          nome: "Educação Especial - Creche",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        educacao_especial_pre_escola: {
          nome: "Educação Especial - Pré-Escola",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        educacao_especial_demais: {
          nome: "Educação Especial - Demais Etapas",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        educacao_indigena_quilombola_creche: {
          nome: "Ed. Indígena/Quilombola - Creche",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        educacao_indigena_quilombola_pre_escola: {
          nome: "Ed. Indígena/Quilombola - Pré-Escola",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        educacao_indigena_quilombola_demais: {
          nome: "Ed. Indígena/Quilombola - Demais",
          fp_vaaf: 1.4,
          fp_vaat: 1.4,
        },
        eja_processo: {
          nome: "EJA com Avaliação no Processo",
          fp_vaaf: 1.0,
          fp_vaat: 1.0,
        },
        eja_integrada_profissional: {
          nome: "EJA Integrada à Ed. Profissional",
          fp_vaaf: 1.35,
          fp_vaat: 1.35,
        },
        formacao_tecnica_profissional: {
          nome: "Formação Técnica e Profissional",
          fp_vaaf: 1.35,
          fp_vaat: 1.35,
        },
      },

      ufs: [
        "AC",
        "AL",
        "AP",
        "AM",
        "BA",
        "CE",
        "DF",
        "ES",
        "GO",
        "MA",
        "MT",
        "MS",
        "MG",
        "PA",
        "PB",
        "PR",
        "PE",
        "PI",
        "RJ",
        "RN",
        "RS",
        "RO",
        "RR",
        "SC",
        "SP",
        "SE",
        "TO",
      ],
      condicionalidades: [
        "<strong>I - Gestão Escolar:</strong> Provimento do cargo de diretor de escola por meio de critérios técnicos de mérito e desempenho",
        "<strong>II - Participação dos Estudantes:</strong> Participação dos Estudantes: Participação em avaliações e no Sistema de Avaliação da Educação Básica (Saeb)",
        "<strong>III - Redução de Desigualdades:</strong> Redução de Desigualdades: Melhoria dos resultados de aprendizagem e redução das desigualdades",
        "<strong>IV - Regime de Colaboração:</strong> Regime de Colaboração: Estabelecimento de acordo de cooperação para implementação de ações",
        "<strong>V - Currículo BNCC:</strong> Implementação do currículo alinhado à Base Nacional Comum Curricular",
      ],
      VALORES_MINIMOS: { vaaf_min: 5447.98, vaat_min: 8006.05 },
      PERCENTUAIS_COMPLEMENTACAO: {
        vaaf: 0.1, // 10%
        vaat: 0.09, // 9%
        vaar: 0.02, // 2%
      },

      resultado: null,
      resumo: [],
      modalidadesEspeciais: [],
      oportunidades: [],
      analiseMatriculas: { total: 0, vaaf: 0, vaat: 0 },
    };
  },

  methods: {
    calcularOrcamento() {
      const { municipio } = this;

      const matriculasPonderadasVAAF = this.calcularMatriculasPonderadas(municipio, "vaaf");
      const matriculasPonderadasVAAT = this.calcularMatriculasPonderadas(municipio, "vaat");

      const complementoVAAF = this.calcularComplemento(municipio, "vaaf", matriculasPonderadasVAAF);
      const complementoVAAT = this.calcularComplemento(municipio, "vaat", matriculasPonderadasVAAT);
      const complementoVAAR = this.calcularComplementoVAAR(municipio, matriculasPonderadasVAAT);

      const incrementoTotal = complementoVAAF.valor + complementoVAAT.valor + complementoVAAR.valor;
      const orcamentoTotal = municipio.receitas_educacao + incrementoTotal;
      const incrementoPercentual = (orcamentoTotal / municipio.receitas_educacao - 1) * 100;

      // Atualizando o objeto de resultado com os valores reais
      this.resultado = {
        municipio: municipio.nome,
        uf: municipio.uf,
        orcamentoBase: municipio.receitas_educacao,
        incrementoTotal,
        orcamentoTotal,
        incrementoPercentual,
        complementos: {
          vaaf: {
            valor: complementoVAAF.valor,
            recebe: complementoVAAF.recebe,
            label: "VAAF - Valor Anual por Aluno Fixo (10%)",
            mensagem: `Matrículas Ponderadas: ${matriculasPonderadasVAAF.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
          },
          vaat: {
            valor: complementoVAAT.valor,
            recebe: complementoVAAT.recebe,
            label: "VAAT - Valor Anual Total por Aluno (9%)",
            mensagem: `Matrículas Ponderadas: ${matriculasPonderadasVAAT.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
          },
          vaar: {
            valor: complementoVAAR.valor,
            recebe: complementoVAAR.recebe,
            label: "VAAR - Valor Aluno-Ano por Resultado (2%)",
            mensagem: "Depende do cumprimento das 5 condicionalidades",
          },
        },
      };

      // Preenchendo os dados para exibição
      this.preencherResumo();
      this.preencherModalidadesEspeciais();
      this.preencherOportunidades();
      this.preencherAnaliseMatriculas();
    },

    calcularMatriculasPonderadas(municipio, modalidade) {
      let total = 0;
      Object.entries(municipio.matriculas_por_segmento).forEach(([key, quantidade]) => {
        if (this.fatoresPonderacao[key]) {
          const fator = this.fatoresPonderacao[key];
          const fp = modalidade === "vaaf" ? fator.fp_vaaf : fator.fp_vaat;
          total += quantidade * fp;
        }
      });
      return total;
    },

    calcularComplemento(municipio, modalidade, matriculasPonderadas) {
      const valorPorAluno = municipio.receitas_educacao / matriculasPonderadas;
      const valorMinimo =
        modalidade === "vaaf" ? this.VALORES_MINIMOS.vaaf_min : this.VALORES_MINIMOS.vaat_min;
      const precisaComplemento = valorPorAluno < valorMinimo;

      if (precisaComplemento) {
        const complemento = (valorMinimo - valorPorAluno) * matriculasPonderadas;
        return { valor: complemento, recebe: true };
      }

      return { valor: 0, recebe: false };
    },

    calcularComplementoVAAR(municipio, matriculasPonderadas) {
      const condicionalidadesCumpridas = municipio.condicionalidades.filter((c) => c).length === 5;
      if (condicionalidadesCumpridas) {
        const valorVaarPorAluno =
          this.VALORES_MINIMOS.vaat_min * this.PERCENTUAIS_COMPLEMENTACAO.vaar;
        const complementoVaar = valorVaarPorAluno * matriculasPonderadas;
        return { valor: complementoVaar, recebe: true };
      }
      return { valor: 0, recebe: false };
    },

    // Funções para preencher os dados exibidos no template
    preencherResumo() {
      this.resumo = [
        {
          titulo: "Orçamento Base",
          valor: this.formatarMoeda(this.resultado.orcamentoBase),
          color: "blue-lighten-5",
          borderColor: "#2196F3",
          valueColor: "#000",
        },
        {
          titulo: "Incremento Total",
          valor: this.formatarMoeda(this.resultado.incrementoTotal),
          color: "green-lighten-5",
          borderColor: "#4caf50",
          valueColor: "#388e3c",
        },
        {
          titulo: "Orçamento Total",
          valor: this.formatarMoeda(this.resultado.orcamentoTotal),
          color: "yellow-lighten-5",
          borderColor: "#fbc02d",
          valueColor: "#f57f17",
        },
        {
          titulo: "Incremento %",
          valor: Number.isNaN(this.resultado.incrementoPercentual)
            ? "0.00%"
            : `${this.resultado.incrementoPercentual.toFixed(2)}%`,
          color: "purple-lighten-5",
          borderColor: "#9c27b0",
          valueColor: "#6a1b9a",
        },
      ];
    },

    preencherModalidadesEspeciais() {
      this.modalidadesEspeciais = [
        "Educação especial (fatores 1,40-1,40)",
        "Educação indígena e quilombola (fatores 1,40-1,40)",
        "Educação do campo (+15% em todos os fatores)",
        "Formação técnica e profissional (fator 1,35)",
      ];
    },

    preencherOportunidades() {
      // Calcula quantas condicionalidades faltam para VAAR
      const condicionalidadesFaltando = this.municipio.condicionalidades.filter(
        (cond) => !cond
      ).length;

      // Atualiza as oportunidades com base nos resultados
      this.oportunidades = [
        {
          tipo: "✅ Elegível para VAAF (10%)",
          valor: this.formatarMoeda(this.resultado.complementos.vaaf.valor),
          elegivel: this.resultado.complementos.vaaf.recebe,
          observacao: "",
        },
        {
          tipo: "✅ Elegível para VAAT (9%)",
          valor: this.formatarMoeda(this.resultado.complementos.vaat.valor),
          elegivel: this.resultado.complementos.vaat.recebe,
          observacao: "",
        },
        {
          tipo: condicionalidadesFaltando
            ? "❌ Não elegível para VAAR"
            : "✅ Elegível para VAAR (2%)",
          valor: this.formatarMoeda(this.resultado.complementos.vaar.valor),
          elegivel: this.resultado.complementos.vaar.recebe,
          observacao: this.resultado.complementos.vaar.recebe
            ? ""
            : `Necessário cumprir ${condicionalidadesFaltando} condicionalidade(s) adicional(is)`,
        },
      ];
    },

    preencherAnaliseMatriculas() {
      // Calculando o total de matrículas
      const totalMatriculas = Object.values(this.municipio.matriculas_por_segmento).reduce(
        (sum, val) => sum + val,
        0
      );

      // Obtendo os segmentos e ordenando os 5 com mais matrículas
      const matriculasOrdenadas = Object.entries(this.municipio.matriculas_por_segmento)
        .filter(([_, quantidade]) => quantidade > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);

      // Atribuindo os segmentos à análise
      const segmentos = {};
      matriculasOrdenadas.forEach(([segmento, quantidade]) => {
        segmentos[segmento] = quantidade;
      });

      // Atribuindo os valores de total e matrículas ponderadas para VAAF e VAAT
      this.analiseMatriculas = {
        total: totalMatriculas,
        vaaf: this.calcularMatriculasPonderadas(this.municipio, "vaaf"),
        vaat: this.calcularMatriculasPonderadas(this.municipio, "vaat"),
        segmentos,
      };
    },

    calcularPercentual(quantidade) {
      // Calcula o percentual de matrículas por segmento
      const totalMatriculas = this.analiseMatriculas.total;
      return totalMatriculas > 0 ? ((quantidade / totalMatriculas) * 100).toFixed(1) : 0;
    },

    formatarMoeda(valor) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(valor);
    },
  },
};
</script>

<style></style>
